"use client";

import { getLink } from "@utils/linkUtils";
import { useLocale } from "next-intl";
import dynamic from "next/dynamic";
import { FC, useState } from "react";
import SwiperCore from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { CardCarouselBottomNavigation } from "@/components/AutogeneratedCarousel/CardCarouselBottomNavigation";
import EditorialCardList from "@/components/AutogeneratedCarousel/EditorialCardList";
import ProjectCardList from "@/components/AutogeneratedCarousel/ProjectCardList";
import UnitTypesCardList from "@/components/AutogeneratedCarousel/UnitTypeCardList";
import VideoCardList from "@/components/AutogeneratedCarousel/VideoCardList";
import AvailableUnitListProvider from "@/context/AvailableUnitListContext";
import useMediaQuery from "@/hooks/useMediaQuery";
import { AutogeneratedCardsProps, CardType, VideoModalDialogProps } from "@/types";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";

const VideoModalDialog = dynamic(() => import("./VideoModalDialog"), { ssr: false });

SwiperCore.use([Pagination, Navigation]);

const AutogeneratedCards: FC<AutogeneratedCardsProps> = ({
  id,
  cardType,
  cards,
  cta,
  variant,
  highlightProjectTag,
  showAsCardList,
  serverSideCookieCurrency,
  currencyFromApiRes,
}) => {
  const isTablet = useMediaQuery("(min-width: 768px)");
  const locale = useLocale();
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [videoItemModal, setVideoModal] = useState<VideoModalDialogProps | null>(null);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [language, _] = locale.split("-");
  // pre-filtered list variant
  if (showAsCardList) {
    return (
      <>
        <div className="grid grid-cols-1 gap-l md:grid-cols-4">
          {cards?.map((item, index) => (
            <div key={`pre-filtered-${index}`}>
              {cardType === CardType.PROJECT && item?.images && item?.bedrooms && (
                <ProjectCardList
                  item={{
                    ...item,
                    images: item.images,
                    bedrooms: item.bedrooms,
                    tags: item.tags?.map((item) => ({ isHiglighted: !!highlightProjectTag, label: item })),
                  }}
                />
              )}
              {cardType === CardType["UNIT-TYPE"] && item?.images && item?.bedrooms && item?.price && (
                <UnitTypesCardList
                  item={{
                    ...item,
                    images: item.images,
                    bedrooms: item.bedrooms,
                    price: item.price,
                    tags: item.tags?.map((item) => ({ isHiglighted: !!highlightProjectTag, label: item })),
                  }}
                  serverSideCookieCurrency={serverSideCookieCurrency}
                  currencyFromApiRes={currencyFromApiRes}
                />
              )}
            </div>
          ))}
        </div>
        {cta && !variant && (
          <div className="mt-xl flex justify-center">
            <Button size={ButtonSize.MEDIUM} theme={ButtonTheme.BLACK} type="button" variant={ButtonVariant.PRIMARY} href={getLink(cta, locale)}>
              {cta.label}
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {videoItemModal && <VideoModalDialog title={videoItemModal?.title} video={videoItemModal?.video} closeModal={() => setVideoModal(null)} />}

      <Swiper
        className={`generated-carousel generated-carousel-${cardType} md:!pl-m`}
        slidesPerView={cardType === CardType.VIDEO && isDesktop ? 1.2 : "auto"}
        spaceBetween={isTablet ? 22 : 16}
        navigation={{
          nextEl: `.swiper-button-next-${id}`,
          prevEl: `.swiper-button-prev-${id}`,
        }}
        pagination={{ type: "progressbar", el: `.swiper-pagination-carousel-${id}` }}
        onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
        onSwiper={(swiper) => setCurrentSlide(swiper.activeIndex)}
        centeredSlides={isMobile ? true : false}
        centerInsufficientSlides={true}
        autoHeight={false}
        watchSlidesProgress={true}
        slidesOffsetAfter={isMobile ? 16 : 0}
      >
        {cards?.map((item, index) => (
          <SwiperSlide className={cardType === CardType.VIDEO && isTablet ? "" : "!w-fit"} key={`autogenerated-${index}`}>
            {cardType === CardType.PROJECT && item?.images && item?.bedrooms && (
              <ProjectCardList
                item={{
                  ...item,
                  images: item.images,
                  bedrooms: item.bedrooms,
                  tags: item.tags?.map((item) => ({ isHiglighted: !!highlightProjectTag, label: item })),
                }}
                customClass="w-[283px] h-[260px] md:w-[255px] md:h-[453px]"
              />
            )}
            {cardType === CardType["UNIT-TYPE"] && item?.images && item?.bedrooms && item?.price && (
              // <AvailableUnitListProvider category={item.categoryID} locale={locale}>
              <UnitTypesCardList
                item={{
                  ...item,
                  images: item.images,
                  bedrooms: item.bedrooms,
                  price: item.price,
                  tags: item.tags?.map((item) => ({ isHiglighted: !!highlightProjectTag, label: item })),
                }}
                customClass={`${cards?.length > 1 ? "w-[283px]" : ""} md:w-[276px] ml-s md:my-l ${language === "ar" ? "mr-[14px]" : ""}`}
                serverSideCookieCurrency={serverSideCookieCurrency}
                currencyFromApiRes={currencyFromApiRes}
              />
              // </AvailableUnitListProvider>
            )}
            {cardType === CardType.EDITORIAL && <EditorialCardList item={item} />}
            {cardType === CardType.VIDEO && (
              <VideoCardList entry={item} isCurrentSlide={currentSlide === index} openVideoModal={(v: VideoModalDialogProps) => setVideoModal(v)} />
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      <CardCarouselBottomNavigation
        carouselId={id || ""}
        customClass={cardType === CardType["UNIT-TYPE"] ? "mt-0" : cardType === CardType.VIDEO ? "mt-[22px] md:mt-0" : "mt-xl"}
        cta={cta}
        variant={variant}
      />
    </>
  );
};

export default AutogeneratedCards;
